import {
  CONFIGURACOES_PADRAO,
  type Configuracoes,
  type ContatoDetalheDTO,
  type ContatoResumoDTO,
  janelaAberta,
} from '@viniko/shared';
import type {
  AtendenteDTO,
  ContagemAbasDTO,
  ConversaDetalheDTO,
  ConversaResumoDTO,
  ListarConversasInput,
} from '@viniko/shared';
import { useQuery } from '@tanstack/react-query';
import { get } from './api';

export const chaves = {
  eu: ['eu'] as const,
  atendentes: ['atendentes'] as const,
  respostas: ['respostas-rapidas'] as const,
  configuracoes: ['configuracoes'] as const,
  contagem: ['conversas', 'contagem'] as const,
  lista: (f: Partial<ListarConversasInput>) => ['conversas', 'lista', f] as const,
  conversa: (id: string) => ['conversa', id] as const,
};

export const useEu = () =>
  useQuery({ queryKey: chaves.eu, queryFn: () => get<AtendenteDTO>('/api/eu'), retry: false });

export const useAtendentes = () =>
  useQuery({ queryKey: chaves.atendentes, queryFn: () => get<AtendenteDTO[]>('/api/atendentes') });

export const useRespostasRapidas = () =>
  useQuery({
    queryKey: chaves.respostas,
    queryFn: () => get<{ id: string; atalho: string; texto: string }[]>('/api/respostas-rapidas'),
    staleTime: 5 * 60_000,
  });

export interface Etiqueta {
  id: string;
  nome: string;
  cor: string;
}

export const useEtiquetas = () =>
  useQuery({
    queryKey: ['etiquetas'],
    queryFn: () => get<Etiqueta[]>('/api/etiquetas'),
    staleTime: 60_000,
  });

export const useConfiguracoes = () =>
  useQuery({
    queryKey: chaves.configuracoes,
    queryFn: () => get<Configuracoes>('/api/configuracoes'),
    staleTime: 60_000,
  });

export const useLimitesFila = () => useConfiguracoes().data?.fila ?? CONFIGURACOES_PADRAO.fila;

export function useContatos(f: { busca?: string; filtro?: string }) {
  const qs = new URLSearchParams(Object.entries(f).filter((e): e is [string, string] => !!e[1]));
  return useQuery({
    queryKey: ['contatos', 'lista', f],
    queryFn: () => get<ContatoResumoDTO[]>(`/api/contatos?${qs}`),
    placeholderData: (anterior) => anterior,
  });
}

export const useContato = (id: string | undefined) =>
  useQuery({
    queryKey: ['contatos', 'detalhe', id],
    queryFn: () => get<ContatoDetalheDTO>(`/api/contatos/${id}`),
    enabled: !!id,
  });

export const useContagem = () =>
  useQuery({
    queryKey: chaves.contagem,
    queryFn: () => get<ContagemAbasDTO>('/api/conversas/contagem'),
  });

export function useListaConversas(f: Partial<ListarConversasInput>) {
  const qs = new URLSearchParams(
    Object.entries(f).filter((e): e is [string, string] => typeof e[1] === 'string' && e[1] !== ''),
  );
  return useQuery({
    queryKey: chaves.lista(f),
    queryFn: () => get<ConversaResumoDTO[]>(`/api/conversas?${qs}`),
    placeholderData: (anterior) => anterior,
  });
}

export const useConversa = (id: string | undefined) =>
  useQuery({
    queryKey: chaves.conversa(id ?? ''),
    queryFn: () => get<ConversaDetalheDTO>(`/api/conversas/${id}`),
    enabled: !!id,
  });

const useSaude = () =>
  useQuery({
    queryKey: ['saude'],
    queryFn: () => get<{ janela24h?: boolean }>('/api/saude'),
    staleTime: Infinity,
  });

/** Igual ao janelaAberta, mas sempre aberta quando a trava das 24h está desligada no servidor. */
export function useJanelaAberta() {
  const ligada = useSaude().data?.janela24h ?? true;
  return (ultima: Date | string | null, agora?: Date) => !ligada || janelaAberta(ultima, agora);
}
